"use client";

/**
 * Tiny wrapper for browser-side JSON fetches.
 *
 * Standardises:
 *   - the Content-Type header on POST/PATCH/PUT
 *   - JSON.stringify on the body
 *   - the post-parse `await res.json().catch(() => ({}))` shape
 *   - surface of `.error` from the response body
 *
 * Returns a `{ ok, status, data, error }` envelope rather than throwing,
 * so call-sites stay flat:
 *
 *   const res = await api.post("/api/places/123/activity", body);
 *   if (!res.ok) toast({ variant: "error", description: res.error });
 */

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data: T;
  /** Convenience accessor for `data.error` when the response includes it. */
  error?: string;
}

interface RequestOptions {
  signal?: AbortSignal;
  /** Extra headers; Content-Type is set automatically when a body is sent. */
  headers?: Record<string, string>;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const init: RequestInit = {
    method,
    signal: options.signal,
  };

  const headers: Record<string, string> = { ...(options.headers ?? {}) };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  if (Object.keys(headers).length > 0) {
    init.headers = headers;
  }

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    // Network failure (offline, DNS, abort). Mimic a fetch-level error.
    return {
      ok: false,
      status: 0,
      data: {} as T,
      error: err instanceof Error ? err.message : "Network error",
    };
  }

  let data: T = {} as T;
  try {
    data = (await res.json()) as T;
  } catch {
    // Empty / non-JSON body — leave data as the empty object.
  }

  const error =
    typeof (data as { error?: unknown })?.error === "string"
      ? ((data as { error: string }).error)
      : undefined;

  return { ok: res.ok, status: res.status, data, error };
}

export const api = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>("GET", path, undefined, options),
  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, body, options),
  patch: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, body, options),
  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PUT", path, body, options),
  delete: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("DELETE", path, body, options),
};
